import React, { useEffect, useMemo, useState } from "react";

import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";

import { useTranslation } from "utils";

import { CoverCropMixType, SoilSightTillage } from "consts";

import type { SoilSightCoverCropTiming } from "models";

import { AlertBanner, Button, Icon, Tooltip } from "components";

import { InfoTooltipIcon } from "pages/app/scenario-analysis/components/InfoTooltipIcon";
import { SortableYearRow } from "pages/app/scenario-analysis/components/crop-rotation/SortableYearRow";
import YearRow from "pages/app/scenario-analysis/components/crop-rotation/YearRow";
import type { CropYear, RecommendedPracticeField, ScenarioMainCropOption } from "pages/app/scenario-analysis/types/scenario";
import { getCoverCropFitByYearId } from "pages/app/scenario-analysis/utils/coverCropFit";
import { createEmptyYear, isResidueLockedToLeftOnField } from "pages/app/scenario-analysis/utils/cropYearHelpers";

export interface CropRotationTableProps {
  years: CropYear[];
  mainCropOptions: ScenarioMainCropOption[];
  isMainCropOptionsLoading?: boolean;
  onYearDataChange?: (yearId: string, updates: Partial<CropYear>) => void;
  onYearRemove?: (yearId: string) => void;
  onYearRefine?: (yearId: string) => void;
  onYearReorder?: (fromIndex: number, toIndex: number) => void;
  onAddYear?: () => void;
  invalidYearIds?: string[];
  tillageErrorYearIds?: string[];
  residueErrorYearIds?: string[];
  addYearErrorMessage?: string;
  validationErrorMessage?: string;
  maxYears?: number;
  dataTestId?: string;
  className?: string;
  /** Onboarding: practice fields to visually mark as auto-recommended, keyed by year id. */
  recommendedFieldsByYearId?: Record<string, RecommendedPracticeField[]>;
  /** Onboarding: the refine drawer is not part of the get-started flow. */
  hideRefineAction?: boolean;
  /** Onboarding step 3: rows mirror the baseline, so deleting them has no meaning. */
  hideDeleteAction?: boolean;
  /** Onboarding step 3: crops are fixed by the baseline — only practices may change. */
  disableCropSelection?: boolean;
  /** Onboarding drops the heading tooltips and the economics column, and keeps add-year inside the table. */
  variant?: "builder" | "onboarding";
  /** Scenarios tab: Silver's cover-crop timing; rows warn when the model would skip their cover crop. */
  coverCropTiming?: SoilSightCoverCropTiming | null;
}

const headerLabel = (text: string, withTooltip: boolean) =>
  withTooltip ? (
    <Tooltip title={text} placement="top" className="scenario-analysis-header-cell-label">
      {text}
    </Tooltip>
  ) : (
    <span className="scenario-analysis-header-cell-label">{text}</span>
  );

export const CropRotationTable = ({
  years,
  onYearDataChange,
  onYearRemove,
  onYearRefine,
  onYearReorder,
  onAddYear,
  invalidYearIds = [],
  tillageErrorYearIds = [],
  residueErrorYearIds = [],
  addYearErrorMessage,
  validationErrorMessage,
  maxYears = 10,
  dataTestId = "",
  className = "",
  variant = "builder",
  mainCropOptions,
  isMainCropOptionsLoading = false,
  recommendedFieldsByYearId,
  hideRefineAction,
  hideDeleteAction,
  disableCropSelection,
  coverCropTiming = null,
}: CropRotationTableProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const isBuilder = variant === "builder";
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = years.findIndex((y) => y.id === active.id);
    const toIndex = years.findIndex((y) => y.id === over.id);
    if (fromIndex !== -1 && toIndex !== -1) {
      onYearReorder?.(fromIndex, toIndex);
    }
  };

  const coverCropFitByYearId = useMemo(() => (coverCropTiming ? getCoverCropFitByYearId(years, coverCropTiming) : {}), [coverCropTiming, years]);

  const showResidueLockInfo = years.some((year) => isResidueLockedToLeftOnField(mainCropOptions, year.crop));

  const sharedYearRowProps = (year: (typeof years)[number], index: number) => ({
    yearNumber: index + 1,
    totalYears: years.length,
    crop: year.crop,
    hasUnresolvableCrop: year.hasUnresolvableCrop,
    mainCropOptions,
    isMainCropOptionsLoading,
    coverCropId: year.coverCropId,
    tillageId: year.tillageId,
    residue: year.residue,
    irrigation: year.irrigation,
    onCropChange: (value: string, lockedResidue?: boolean) =>
      onYearDataChange?.(year.id, { crop: value, ...(lockedResidue !== undefined ? { residue: lockedResidue } : {}) }),
    onCoverCropIdChange: (value: string) => onYearDataChange?.(year.id, { coverCropId: value as CoverCropMixType }),
    onTillageChange: (value: string) => onYearDataChange?.(year.id, { tillageId: value as SoilSightTillage }),
    onResidueChange: (value: boolean) => onYearDataChange?.(year.id, { residue: value }),
    onIrrigationChange: (value: boolean) => onYearDataChange?.(year.id, { irrigation: value }),
    onRefine: () => onYearRefine?.(year.id),
    onRemove: () => onYearRemove?.(year.id),
    onClear: () => onYearDataChange?.(year.id, createEmptyYear(year.id)),
    cropError: invalidYearIds.includes(year.id),
    residueError: residueErrorYearIds.includes(year.id),
    tillageError: tillageErrorYearIds.includes(year.id),
    dataTestId: `${dataTestId}-year-${index + 1}`,
    recommendedFields: recommendedFieldsByYearId?.[year.id],
    variant,
    hideRefineAction,
    hideDeleteAction,
    disableCropSelection,
    coverCropFit: coverCropFitByYearId[year.id],
  });

  // The builder keeps this outside the bordered table, so the border closes after the last crop row.
  const addYearBlock = onAddYear ? (
    <div className="scenario-analysis-add-year">
      {validationErrorMessage && (
        <AlertBanner
          type="error"
          description={validationErrorMessage}
          iconName="tabler/alert-circle"
          className="scenario-analysis-add-year-validation"
          dataTestId={dataTestId ? `${dataTestId}-validation-error` : undefined}
        />
      )}
      {addYearErrorMessage && (
        <AlertBanner type="error" description={addYearErrorMessage} iconName="tabler/alert-circle" className="scenario-analysis-add-year-validation" />
      )}
      <div className="scenario-analysis-add-year-actions">
        <Button
          className="scenario-analysis-table-text-link"
          type="text"
          onClick={years.length < maxYears ? onAddYear : undefined}
          disabled={years.length >= maxYears}
          dataTestId={dataTestId ? `${dataTestId}-add-year` : undefined}
        >
          <Icon iconName="klim/plus" size="s" className="scenario-analysis-add-year-plus-icon" aria-hidden />
          {t("scenarioBuilder.addYear")}
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className={`scenario-analysis-table ${className}`}>
        {/* Table Header */}
        <div className="scenario-analysis-table-header">
          <div className="scenario-analysis-header-cell year">{headerLabel(t("scenarioBuilder.yearColumn"), isBuilder)}</div>
          <div className="scenario-analysis-header-cell crop">{headerLabel(t("scenarioBuilder.crop"), isBuilder)}</div>
          <div className="scenario-analysis-header-cell residue">
            {headerLabel(t("scenarioBuilder.residue"), isBuilder)}
            {showResidueLockInfo && (
              <InfoTooltipIcon title={t("scenarioBuilder.residueLockedLeftOnFieldTooltip")} dataTestId="crop-rotation-residue-lock-info" />
            )}
          </div>
          <div className="scenario-analysis-header-cell tillage">{headerLabel(t("scenarioBuilder.tillage"), isBuilder)}</div>
          <div className="scenario-analysis-header-cell cover">
            {headerLabel(t("scenarioBuilder.coverCrop"), isBuilder)}
            <InfoTooltipIcon title={t("scenarioBuilder.coverCropTimingTooltip")} dataTestId="crop-rotation-cover-crop-info" />
          </div>
          <div className="scenario-analysis-header-cell irrigation">{headerLabel(t("scenarioBuilder.irrigation"), isBuilder)}</div>
          {isBuilder ? (
            <>
              <div className="scenario-analysis-header-cell delete" aria-hidden />
              <div className="scenario-analysis-header-cell economics">
                {headerLabel(t("scenarioBuilder.editEconomicInputsPerCrop"), isBuilder)}
                <InfoTooltipIcon title={t("scenarioBuilder.editEconomicInputsTooltip")} dataTestId="crop-rotation-economics-info" />
              </div>
            </>
          ) : (
            <div className="scenario-analysis-header-cell actions" aria-hidden />
          )}
        </div>

        {/* Year Rows */}
        <div className="scenario-analysis-year-rows">
          {isMounted ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={years.map((y) => y.id)} strategy={verticalListSortingStrategy}>
                {years.map((year, index) => (
                  <SortableYearRow key={year.id} id={year.id} {...sharedYearRowProps(year, index)} />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            years.map((year, index) => <YearRow key={year.id} {...sharedYearRowProps(year, index)} />)
          )}
        </div>

        {!isBuilder && addYearBlock}
      </div>
      {isBuilder && addYearBlock}
    </>
  );
};

export default CropRotationTable;
